import { useEffect, useRef, useState } from 'react';
import { Paperclip, Send } from 'lucide-react';
import type { TaskMember } from '@shared/types/tasks';
import { TASK_SOURCE_LABELS, type TaskUpstream } from '@shared/types/task-sources';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { deleteTaskAttachment } from '@/lib/tasks-api';
import { readPref, UI_PREFS, writePref } from '@/lib/ui-prefs';
import { useAuthStore } from '@/stores/authStore';
import { attachFilesToTask, sendMessage, uploadFile, withinSizeLimit } from './task-files';
import { MentionText } from './MentionText';
import { MentionTextarea } from './MentionTextarea';
import { displayToMarkup, markupToDisplay, type PickedMention } from './mention-text';
import { clipboardFiles, pendingKey } from './clipboard-files';
import { PendingFiles, type PendingFile } from './PendingFiles';
import { useStagedFiles } from './staged-files';
import { commentTooLong } from './comment-length';

/**
 * Chat box: `@` mentions, Cmd/Ctrl+Enter to send, attach files. Pasting or dropping a file in the
 * box stages it with the message; pasting anywhere else in the task posts it straight away
 * (dropping elsewhere is handled by TaskDetailView). Preview shows the draft as the timeline will,
 * markdown rendered (T-9). The unsent message is kept in this browser per task until it's sent, so
 * closing the Tasks panel, opening the chat over it or switching tasks doesn't lose it (SF-T-139).
 */
export function TaskChatComposer({
  taskId,
  members,
  upstream = null,
}: {
  taskId: string;
  members: TaskMember[];
  /** A mirrored task's source (T-15): offers "Also post to …" for this message. */
  upstream?: Pick<TaskUpstream, 'kind'> | null;
}) {
  const me = useAuthStore((s) => s.user?.id);
  const draftKey = { task: taskId, scopeId: me };
  const [draft, setDraft] = useState(() => ({ taskId, ...markupToDisplay(readPref(UI_PREFS.tasksCommentDraft, draftKey)) }));
  // Another task in the same box: its own kept message.
  if (draft.taskId !== taskId) setDraft({ taskId, ...markupToDisplay(readPref(UI_PREFS.tasksCommentDraft, draftKey)) });
  const { text, mentions } = draft;
  const change = (next: { text?: string; mentions?: PickedMention[] }) => setDraft((d) => ({ ...d, ...next }));
  // Kept in this browser as it changes (storage is an outside system); empty removes it.
  const markup = displayToMarkup(text, mentions);
  useEffect(() => {
    writePref(UI_PREFS.tasksCommentDraft, markup, { task: draft.taskId, scopeId: me });
  }, [markup, draft.taskId, me]);
  const setText = (value: string) => change({ text: value });
  const setMentions = (value: PickedMention[]) => change({ mentions: value });
  // Off for every message: the timeline is the factory's, and upstream gets only what's chosen.
  const [postUpstream, setPostUpstream] = useState(false);
  // Kept by task outside the box, as the text is, so the box rebuilding doesn't drop them (staged-files).
  const [pending, setPending] = useStagedFiles(taskId);
  const [view, setView] = useState<'write' | 'preview'>('write');
  const fileInput = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  // A paste anywhere in this task (not already taken by the chat box) attaches the file.
  useEffect(() => {
    const onPaste = (event: ClipboardEvent) => {
      if (event.defaultPrevented) return;
      const view = rootRef.current?.closest('[data-task-view]');
      const target = event.target as Node | null;
      // Inside the task, or focus on something that contains it (the dialog, or the page body).
      if (!view || !target || !(view.contains(target) || target.contains(view))) return;
      const files = clipboardFiles(event.clipboardData);
      if (!files.length) return;
      event.preventDefault();
      attachFilesToTask(taskId, files);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [taskId]);

  /** Files start uploading as soon as they're added; Send doesn't wait for them. */
  const stage = (files: File[]) => {
    for (const file of withinSizeLimit(files)) {
      const key = pendingKey(file);
      const upload = uploadFile(taskId, file).then((attachmentId) => {
        setPending((p) =>
          attachmentId
            ? p.map((x) => (x.key === key ? { ...x, attachmentId, uploading: false } : x))
            : p.filter((x) => x.key !== key)
        );
        return attachmentId;
      });
      setPending((p) => [...p, { key, file, attachmentId: null, uploading: true, upload }]);
    }
  };

  const removePending = (item: PendingFile) => {
    setPending((p) => p.filter((x) => x.key !== item.key));
    void item.upload?.then((attachmentId) => {
      if (attachmentId) void deleteTaskAttachment(taskId, attachmentId);
    });
  };

  const tooLong = commentTooLong(displayToMarkup(text.trim(), mentions));
  const canSend = (text.trim().length > 0 || pending.length > 0) && !tooLong;

  /** The message shows in the timeline at once; uploading and saving carry on behind it. */
  const send = () => {
    if (!canSend) return;
    const alsoUpstream = upstream && postUpstream ? { label: TASK_SOURCE_LABELS[upstream.kind] } : undefined;
    sendMessage(taskId, displayToMarkup(text.trim(), mentions), pending, alsoUpstream);
    setPostUpstream(false);
    change({ text: '', mentions: [] });
    setPending(() => []);
    setView('write');
  };

  return (
    <Tabs
      ref={rootRef}
      value={view}
      onValueChange={(v) => setView(v === 'preview' ? 'preview' : 'write')}
      className="gap-2 rounded-lg border bg-card p-2 shadow-xs focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/20"
    >
      <TabsContent value="write">
        <MentionTextarea
          aria-label="Message"
          tagging
          aiMachines
          value={text}
          onChange={setText}
          mentions={mentions}
          onMentionsChange={setMentions}
          members={members}
          rows={2}
          placeholder="Write a message… @ to mention"
          className="border-0 p-1 shadow-none focus-visible:ring-0"
          onSubmit={send}
          onFiles={stage}
        />
      </TabsContent>
      {/* At least the box's height, so switching doesn't make the composer jump. */}
      <TabsContent value="preview" className="max-h-80 min-h-16 overflow-y-auto p-1">
        {text.trim() ? (
          <MentionText text={displayToMarkup(text.trim(), mentions)} members={members} markdown className="text-sm leading-relaxed" />
        ) : (
          <p className="text-sm text-muted-foreground">Nothing to preview yet.</p>
        )}
      </TabsContent>
      <PendingFiles items={pending} onRemove={removePending} />
      {tooLong ? <p className="px-1 text-xs text-destructive">{tooLong}</p> : null}
      {/* Its own row, so the toolbar keeps its room in a narrow activity pane. */}
      {upstream ? (
        <label className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground" data-hint="The message goes on the task upstream too, signed with your name">
          <Checkbox checked={postUpstream} onCheckedChange={(on) => setPostUpstream(on === true)} />
          Also post to {TASK_SOURCE_LABELS[upstream.kind]}
        </label>
      ) : null}
      <div className="flex items-center gap-1">
        <input
          ref={fileInput}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = [...(e.target.files ?? [])];
            e.target.value = '';
            if (files.length) stage(files);
          }}
        />
        <Button
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground"
          onClick={() => fileInput.current?.click()}
          aria-label="Attach files"
          data-hint="Attach files (or paste / drop them)"
        >
          <Paperclip className="size-4" />
        </Button>
        <TabsList size="sm" aria-label="Message view">
          <TabsTrigger value="write">Write</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>
        <span className="ml-auto mr-2 hidden text-xs text-muted-foreground sm:inline">
          {navigator.platform.toLowerCase().includes('mac') ? '⌘' : 'Ctrl'}+Enter to send
        </span>
        <Button size="sm" disabled={!canSend} onClick={send}>
          <Send className="size-4" />
          Send
        </Button>
      </div>
    </Tabs>
  );
}
